import { FC, useCallback, useState } from "react";
import { Stack, Theme, Typography } from "@mui/material";
import { ColDef, GridApi, RowClickedEvent } from "ag-grid-community";
import { useEffectOnceWhen, usePreviousDifferent } from "rooks";
import { AgGridReactProps } from "ag-grid-react";
import { SxProps } from "@mui/system";
import { coverageGroupingNames } from "../utils/constants/labels";
import { AgDataGrid } from "../../../common/ag-grid/AgDataGrid";
import {
  autoGroupColumnDef,
  commitmentsGridDefaultColDef,
  commitmentsGridWrapperStyles,
} from "../utils/constants/gridConstants";
import { commitmentsTotalsFromGridApi } from "../utils/helpers/commitmentsTotalsFromGridApi";
import {
  setCommitmentsDetailsDrawerDates,
  setCommitmentsDetailsRequestData,
  setCommitmentsTotals,
} from "@/store/commitments/commitmentsSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { SavingsPlanCoverageGrouping } from "@/services/cloudchipr.api";
import { commitmentsDatesSelector } from "@/store/commitments/selectors/common/view-options/commitmentsDatesSelector";
import { CommitmentsDetailsRequestData } from "@/store/commitments/utils/types/types";

export type CommitmentsToolbar = FC<{ gridApi: GridApi | null }>;

interface CommitmentsAgGridProps {
  loading?: boolean;
  inDrawer?: boolean;
  columnDefs: ColDef[];
  data: any[] | undefined;
  Toolbar?: CommitmentsToolbar;
  localStorageUniqueKey: string;
  grouping?: SavingsPlanCoverageGrouping;
  overrideWrapperStyles?: SxProps<Theme>;
  clickHandlerDataGetter?(data: any): CommitmentsDetailsRequestData;
  overrideDefaultColDef?: AgGridReactProps["defaultColDef"];
  noRowOverlayComponent?: AgGridReactProps["noRowsOverlayComponent"];
}

export const CommitmentsAgGrid: FC<CommitmentsAgGridProps> = ({
  data,
  inDrawer,
  loading,
  Toolbar,
  grouping,
  columnDefs,
  clickHandlerDataGetter,
  localStorageUniqueKey,
  overrideDefaultColDef,
  overrideWrapperStyles,
  noRowOverlayComponent: customNoRowOverlayComponent,
}) => {
  const dispatch = useAppDispatch();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const clickable = !!clickHandlerDataGetter && !!grouping;

  const prevData = usePreviousDifferent(data);
  const gridData = data ?? prevData;

  const dates = useAppSelector(commitmentsDatesSelector);

  const configToRememberStateInStorage = {
    uniqueNameAsKey: `${localStorageUniqueKey}-${grouping}`,
  };

  const noRowOverlayComponent = useCallback(() => {
    if (!grouping) {
      return;
    }

    return (
      <Typography variant="body2">
        No {coverageGroupingNames[grouping]} found
      </Typography>
    );
  }, [grouping]);

  const calculateTotalsOnEvent = useCallback(() => {
    const totals = commitmentsTotalsFromGridApi(gridApi);

    if (!totals) {
      return;
    }

    dispatch(setCommitmentsTotals(totals));
  }, [gridApi, dispatch]);

  const onRowClicked = useCallback(
    ({ data, event }: RowClickedEvent) => {
      const target = event?.target as HTMLElement;

      if (!clickable || target?.closest("[data-block-row-click]")) {
        return;
      }

      const requestData = clickHandlerDataGetter(data);

      if (!requestData.targetId) {
        return;
      }

      dispatch(setCommitmentsDetailsDrawerDates(dates));
      dispatch(setCommitmentsDetailsRequestData(requestData));
    },
    [clickable, clickHandlerDataGetter, dates, dispatch],
  );

  useEffectOnceWhen(() => {
    const totals = commitmentsTotalsFromGridApi(gridApi);

    if (!totals) {
      return;
    }

    dispatch(setCommitmentsTotals(totals));
  }, !!gridApi);

  // items height + toolbar height + header height
  const calculatedHeight = gridData?.length
    ? (gridData?.length ?? 0) * 60 + 60 + 60
    : 200;

  return (
    <Stack
      minHeight={400}
      maxHeight={inDrawer ? "80dvh" : undefined}
      height="100%"
    >
      {Toolbar && <Toolbar gridApi={gridApi} />}

      <AgDataGrid
        rowHeight={60}
        headerHeight={60}
        loading={loading}
        columnDefs={columnDefs}
        rowData={gridData ?? []}
        rowGroupPanelShow="always"
        gridApiSetter={setGridApi}
        onRowClicked={onRowClicked}
        onGridReady={calculateTotalsOnEvent}
        onFilterChanged={calculateTotalsOnEvent}
        onRowDataUpdated={calculateTotalsOnEvent}
        autoGroupColumnDef={autoGroupColumnDef}
        configToRememberStateInStorage={configToRememberStateInStorage}
        wrapperStyles={{
          commitmentsGridWrapperStyles,
          ...overrideWrapperStyles,
          height: inDrawer ? calculatedHeight : undefined,
        }}
        defaultColDef={{
          ...commitmentsGridDefaultColDef,
          ...overrideDefaultColDef,
          cellStyle: ({ node }) =>
            !node.group && clickable ? { cursor: "pointer" } : undefined,
        }}
        noRowsOverlayComponent={
          customNoRowOverlayComponent ?? noRowOverlayComponent
        }
      />
    </Stack>
  );
};
